<script setup lang="ts">
/**
 * Module: web/src/views/admin/AdminAuditView.vue
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.1.0
 * Date: 2026-07-14
 *
 * Auth audit trail (doc 04 sect 9.1, gated user_admin) -- table over
 * GET /admin/audit?limit=. Wide table scrolls inside its own container on
 * mobile (doc 12 sect 4.1 tier-2 rule).
 *
 * v1.1.0 (Slice C audit rollback, contract audit-rollback-api v1.0.0 sect 8):
 * per-row UNDO -- reversal not erasure. Eligible rows (server-computed
 * static eligibility) get an Undo button; ineligible rows a greyed button
 * whose tooltip carries the matrix reason verbatim; rows already rolled
 * back show a subtle "rolled back by #N" annotation and stay visible
 * forever. Undo opens a confirm dialog fed by GET rollback-preview (the
 * dialog copy IS the server's description -- exactly what will be
 * reversed); Confirm POSTs the rollback and reloads the list so the new
 * audit_rollback + reversal rows appear at the top.
 */
import { onMounted, ref } from 'vue';
import { apiGet, apiPost, unwrapList, ApiError } from '@/composables/useApi';
import type { AuditEvent, RollbackPreview, RollbackResult } from '@/types/api';
import { fmtDateTime, fmtDetail } from '@/lib/format';
import BaseModal from '@/components/BaseModal.vue';
import HelpTip from '@/components/HelpTip.vue';

const events = ref<AuditEvent[]>([]);
const loading = ref(true);
const errorMessage = ref('');
const limit = ref(100);

/** login_ok/pin_login_ok green, *_fail/lockout red, everything else neutral. */
function eventTone(event: string): string {
  if (event.endsWith('_ok')) return 'text-accent-good';
  if (event.includes('fail') || event.includes('lockout')) return 'text-accent-bad';
  return 'text-accent';
}

async function load() {
  loading.value = true;
  errorMessage.value = '';
  try {
    const body = await apiGet<unknown>('/admin/audit', `limit=${limit.value}`);
    events.value = unwrapList<AuditEvent>(body, ['events', 'audit', 'rows']);
  } catch {
    errorMessage.value = 'Could not load the audit log.';
  } finally {
    loading.value = false;
  }
}

// ---- Undo flow (contract sect 8) ------------------------------------------ //

const undoFor = ref<AuditEvent | null>(null);   // modal target (null = closed)
const preview = ref<RollbackPreview | null>(null);
const previewLoading = ref(false);
const undoError = ref('');
const executing = ref(false);
const successNote = ref('');

function undoTooltip(ev: AuditEvent): string {
  if (ev.rollback?.eligible) return `Undo this ${ev.event} action`;
  return ev.rollback?.reason ?? 'not reversible';
}

async function openUndo(ev: AuditEvent) {
  undoFor.value = ev;
  preview.value = null;
  undoError.value = '';
  previewLoading.value = true;
  try {
    preview.value = await apiGet<RollbackPreview>(
      `/admin/audit/${ev.id}/rollback-preview`,
    );
  } catch {
    undoError.value = 'Could not load the undo preview.';
  } finally {
    previewLoading.value = false;
  }
}

function closeUndo() {
  if (executing.value) return; // never abandon an in-flight rollback
  undoFor.value = null;
  preview.value = null;
  undoError.value = '';
}

async function confirmUndo() {
  if (!undoFor.value || !preview.value?.eligible) return;
  executing.value = true;
  undoError.value = '';
  try {
    const res = await apiPost<RollbackResult>(
      `/admin/audit/${undoFor.value.id}/rollback`,
    );
    successNote.value =
      `Rolled back #${undoFor.value.id} (${undoFor.value.event}) — ` +
      `recorded as audit event #${res.rollback.id}.`;
    undoFor.value = null;
    preview.value = null;
    await load(); // the new rollback + reversal rows surface at the top
  } catch (err) {
    // 409 stale_state and friends carry the precise reason in the body.
    undoError.value =
      (err instanceof ApiError &&
        ((err.field('reason') as string | undefined) ?? err.detail ?? undefined)) ||
      'The undo could not be completed.';
  } finally {
    executing.value = false;
  }
}

function currentStateJson(p: RollbackPreview): string {
  try {
    return JSON.stringify(p.target?.current ?? {}, null, 2);
  } catch {
    return '';
  }
}

onMounted(load);
</script>

<template>
  <div class="px-4 py-5 md:p-6 max-w-6xl">
    <div class="flex items-center justify-between gap-3 mb-4">
      <div>
        <h1 class="text-xl font-bold text-fg">Audit Log</h1>
        <p class="text-sm text-fg-muted">Recent auth events — logins, lockouts, admin + PIN actions.</p>
      </div>
      <div class="flex items-center gap-2">
        <select v-model.number="limit" class="field-input !h-11 !w-auto text-sm" @change="load">
          <option :value="50">50</option>
          <option :value="100">100</option>
          <option :value="250">250</option>
        </select>
        <button class="btn-secondary !h-11 !px-3 text-sm" :disabled="loading" @click="load">Refresh</button>
      </div>
    </div>

    <div v-if="errorMessage" class="alert-error mb-4" role="alert">{{ errorMessage }}</div>
    <div v-if="successNote" class="alert-info mb-4" role="status">
      {{ successNote }}
      <button class="underline ml-2" @click="successNote = ''">Dismiss</button>
    </div>
    <div v-if="loading" class="text-sm text-fg-muted py-8 text-center">Loading…</div>

    <div v-else-if="events.length === 0" class="bg-card border border-border-c rounded-lg p-8 text-center">
      <p class="text-sm text-fg-muted">No audit events yet.</p>
    </div>

    <div v-else class="bg-card border border-border-c rounded-lg overflow-hidden">
      <div class="overflow-x-auto">
        <table class="w-full text-sm">
          <thead>
            <tr class="text-left text-xs uppercase tracking-wide text-fg-muted border-b border-border-c">
              <th class="px-4 py-3 font-medium">When</th>
              <th class="px-4 py-3 font-medium">Event</th>
              <th class="px-4 py-3 font-medium">User / email tried</th>
              <th class="px-4 py-3 font-medium">IP</th>
              <th class="px-4 py-3 font-medium">Detail</th>
              <th class="px-4 py-3 font-medium">
                <span class="inline-flex items-center gap-1">
                  Undo
                  <HelpTip k="admin.audit_rollback" />
                </span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="ev in events" :key="ev.id" class="border-b border-border-c/60 last:border-0 hover:bg-input/40 align-top">
              <td class="px-4 py-2.5 text-fg whitespace-nowrap">{{ fmtDateTime(ev.at) }}</td>
              <td class="px-4 py-2.5 whitespace-nowrap">
                <code class="text-xs" :class="eventTone(ev.event)">{{ ev.event }}</code>
                <div
                  v-if="ev.rolled_back_by"
                  class="text-[11px] text-fg-muted mt-0.5"
                >
                  rolled back by #{{ ev.rolled_back_by }}
                </div>
              </td>
              <td class="px-4 py-2.5 text-fg-muted whitespace-nowrap">
                {{ ev.email_tried ?? (ev.user_id !== null && ev.user_id !== undefined ? `user #${ev.user_id}` : '—') }}
              </td>
              <td class="px-4 py-2.5 text-fg-muted whitespace-nowrap">{{ ev.ip ?? '—' }}</td>
              <td class="px-4 py-2.5 text-fg-muted min-w-[12rem]">{{ fmtDetail(ev.detail) }}</td>
              <td class="px-4 py-2.5 whitespace-nowrap">
                <button
                  v-if="ev.rollback?.eligible"
                  class="btn-secondary !h-8 !px-3 text-xs"
                  :title="undoTooltip(ev)"
                  :aria-label="undoTooltip(ev)"
                  @click="openUndo(ev)"
                >
                  Undo
                </button>
                <button
                  v-else
                  class="btn-secondary !h-8 !px-3 text-xs opacity-40 cursor-not-allowed"
                  disabled
                  :title="undoTooltip(ev)"
                  :aria-label="undoTooltip(ev)"
                >
                  Undo
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Undo confirm dialog (contract sect 8): the server's preview
         description IS the copy -- it states exactly what will be reversed. -->
    <BaseModal
      :open="undoFor !== null"
      :title="undoFor ? `Undo audit event #${undoFor.id}` : ''"
      @close="closeUndo"
    >
      <div v-if="previewLoading" class="text-sm text-fg-muted py-6 text-center">
        Checking what this undo would do…
      </div>

      <template v-else-if="preview">
        <p class="text-xs text-fg-muted mb-2">
          <code class="text-xs text-accent">{{ preview.event }}</code>
          <template v-if="preview.rolled_back_by">
            · already rolled back by #{{ preview.rolled_back_by }}
          </template>
        </p>

        <template v-if="preview.eligible">
          <p class="text-sm text-fg mb-3">{{ preview.description }}</p>
          <div v-if="preview.target" class="mb-3">
            <p class="text-xs text-fg-muted mb-1">Current state it was checked against:</p>
            <pre class="text-xs text-fg bg-bg border border-border-c rounded-lg p-3 overflow-x-auto whitespace-pre-wrap break-words">{{ currentStateJson(preview) }}</pre>
          </div>
          <p class="text-xs text-fg-muted">
            This is a reversal, not a deletion — the original event stays in the
            log, and the undo is recorded as its own audit event.
          </p>
        </template>

        <div v-else class="alert-error text-sm" role="alert">
          This action can’t be undone: {{ preview.reason }}
        </div>
      </template>

      <div v-if="undoError" class="alert-error text-sm mt-3" role="alert">{{ undoError }}</div>

      <template #footer>
        <div class="flex items-center justify-end gap-2">
          <button class="btn-secondary !h-10 !px-4 text-sm" :disabled="executing" @click="closeUndo">
            Cancel
          </button>
          <button
            v-if="preview?.eligible"
            class="btn-primary !h-10 !px-4 text-sm"
            :disabled="executing || previewLoading"
            @click="confirmUndo"
          >
            {{ executing ? 'Undoing…' : 'Confirm undo' }}
          </button>
        </div>
      </template>
    </BaseModal>
  </div>
</template>
